import { useCallback, useEffect, useRef, useState } from 'react';
import { Activity, CheckCircle2, CircleHelp, Copy, Cpu, Eraser, Globe, Loader2, Lock, Mail, Network, Pencil, Play, Plus, Power, RefreshCw, Rocket, RotateCw, Trash2, TriangleAlert, Wifi, X, XCircle } from 'lucide-react';
import { CredBlock } from '../components/CredBlock.jsx';
import { api, ApiError } from '../api/client.js';
import { Button } from '../components/Button.jsx';
import { Card, CardHeader } from '../components/Card.jsx';
import { Banner } from '../components/Banner.jsx';
import { Table, THead, TBody, TR, TH, TD, Empty } from '../components/Table.jsx';
import { Input, Textarea, Select, Radio, RadioGroup, InputGroup, FormError } from '../components/Field.jsx';
import { Checkbox } from '../components/Checkbox.jsx';
import { Gauge } from '../components/Gauge.jsx';
import { ServerProxyPanel } from './ServerProxyPanel.jsx';
import { ServerHttpsPanel } from './ServerHttpsPanel.jsx';

/**
 * /servers — external servers registry (hybrid model).
 *   kind 'agent' → full WP management via a vps1-api agent (HTTP+JWT)
 *   kind 'ssh'   → plain SSH target for static-site deploys
 *
 * §13 Phase C, commit C2a of three: the page shell, the table, the row and its
 * action cluster, the two badges, and the add-server form. The expandable
 * panels (Ops / Deploy / Bootstrap / WP / Edit) come in C2b and the mail
 * sub-app in C2c — they still render on their pre-§13 styling until then, which
 * is why this file still imports lucide icons those panels use.
 *
 * §3 note on the action cluster: it used six colour families (indigo, emerald,
 * teal, purple, slate, red) for eight buttons. Colour here is semantic, not
 * decorative — so every action is now `tertiary` and only Delete is `danger`.
 * That is the rule doing real work: the one button that destroys something is
 * now the only coloured one in the row.
 */
export function Servers() {
  const [servers, setServers] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // server ids ticked for bulk delete
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api('/api/servers');
      setServers(data.servers);
      // Drop ticks for servers that no longer exist (deleted here or elsewhere).
      setSelected((prev) => new Set([...prev].filter((id) => data.servers.some((s) => s.id === id))));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'network_error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedServers = (servers ?? []).filter((s) => selected.has(s.id));

  function toggleSelected(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) =>
      prev.size === (servers?.length ?? 0) ? new Set() : new Set(servers.map((s) => s.id)),
    );
  }

  return (
    <div className="space-y-5">
      {error && (
        <Banner kind="error" title="Couldn't load the server registry">
          {error} — nothing has changed; this view only reads. The servers
          themselves and anything running on them are unaffected.
        </Banner>
      )}

      {showForm && <AddServerForm onCreated={() => { setShowForm(false); load(); }} />}

      <Card>
        <CardHeader
          title="External servers"
          chip={servers ? String(servers.length) : undefined}
          description="WP hosting agents and SSH targets for static deploys."
          actions={
            <>
              {selectedServers.length > 0 && (
                <Button variant="danger" icon={Trash2} onClick={() => setBulkOpen(true)}>
                  Delete selected ({selectedServers.length})
                </Button>
              )}
              <Button variant="primary" icon={Plus} onClick={() => setShowForm((v) => !v)}>
                Add server
              </Button>
            </>
          }
        />

        {servers === null ? (
          <div className="flex items-center justify-center gap-2 p-12 text-[13.5px] text-t3">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading servers…
          </div>
        ) : servers.length === 0 ? (
          <div className="p-12 text-center">
            <h3 className="text-[13.5px] font-medium text-t1">No servers yet</h3>
            <p className="mt-1 text-[12.5px] text-t3">
              Add an external server to manage it from the panel.
            </p>
          </div>
        ) : (
          <ServersTable
            servers={servers}
            onChanged={load}
            selected={selected}
            onToggle={toggleSelected}
            onToggleAll={toggleSelectAll}
          />
        )}
      </Card>

      {bulkOpen && selectedServers.length > 0 && (
        <BulkDeleteDialog
          servers={selectedServers}
          onClose={(changed) => {
            setBulkOpen(false);
            if (changed) load();
          }}
        />
      )}
    </div>
  );
}

function ServersTable({ servers, onChanged, selected, onToggle, onToggleAll }) {
  return (
    <Table>
      <THead>
        <TR hover={false}>
          <TH className="w-10">
            <Checkbox
              checked={servers.length > 0 && selected.size === servers.length}
              indeterminate={selected.size > 0}
              onChange={onToggleAll}
              aria-label="Select all servers"
              title="Select all"
            />
          </TH>
          <TH>Name</TH>
          <TH>Kind</TH>
          <TH>Host</TH>
          <TH>Status</TH>
          <TH>Last checked</TH>
          <TH className="text-right">Actions</TH>
        </TR>
      </THead>
      <TBody>
        {servers.map((s) => (
          <ServerRow
            key={s.id}
            server={s}
            onChanged={onChanged}
            selected={selected.has(s.id)}
            onToggle={() => onToggle(s.id)}
          />
        ))}
      </TBody>
    </Table>
  );
}

function ServerRow({ server, onChanged, selected, onToggle }) {
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [deployOpen, setDeployOpen] = useState(false);
  const [bootstrapOpen, setBootstrapOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [wpOpen, setWpOpen] = useState(false);
  const [opsOpen, setOpsOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [proxyOpen, setProxyOpen] = useState(false);
  const [httpsOpen, setHttpsOpen] = useState(false);

  async function handleTest() {
    setBusy(true);
    setTestResult(null);
    try {
      const r = await api(`/api/servers/${server.id}/test`, { method: 'POST' });
      setTestResult(r);
    } catch (err) {
      setTestResult({ ok: false, detail: err.message });
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  async function handleDelete() {
    const typed = window.prompt(
      `Type the server name to confirm deletion:\n${server.name}`,
    );
    if (typed !== server.name) return;
    setBusy(true);
    try {
      await api(`/api/servers/${server.id}`, {
        method: 'DELETE',
        body: { confirm_name: typed },
      });
      onChanged();
    } catch (err) {
      window.alert(`Delete failed: ${err.message}`);
      setBusy(false);
    }
  }

  return (
    <>
    <TR>
      <TD className="w-10">
        <Checkbox checked={selected} onChange={onToggle} aria-label={`Select ${server.name}`} />
      </TD>
      <TD name>{server.name}</TD>
      <TD>
        <KindBadge kind={server.kind} />
      </TD>
      <TD mono>
        {server.host}
        {server.kind === 'ssh' && server.ssh_port !== 22 ? `:${server.ssh_port}` : ''}
      </TD>
      <TD>
        <StatusBadge status={server.status} />
        {testResult && (
          <span className={`ml-2 text-[12px] ${testResult.ok ? 'text-ok' : 'text-err'}`}>
            {testResult.detail}
          </span>
        )}
      </TD>
      <TD className="whitespace-nowrap text-[12.5px]">
        {server.last_checked_at
          ? new Date(`${server.last_checked_at}Z`).toLocaleString()
          : <Empty />}
      </TD>
      <TD>
        <div className="flex items-center justify-end gap-1.5">
          {server.kind === 'ssh' && (
            <>
            <Button
              size="sm"
              icon={Rocket}
              disabled={busy || (!server.has_ssh_key && !server.has_ssh_password)}
              title={server.has_ssh_key || server.has_ssh_password ? 'Deploy a static site' : 'Store an SSH key or password first (edit server)'}
              onClick={() => { setDeployOpen((v) => !v); setBootstrapOpen(false); }}
            >
              Deploy
            </Button>
            <Button
              size="sm"
              icon={Cpu}
              disabled={busy || (!server.has_ssh_key && !server.has_ssh_password)}
              title={server.has_ssh_key || server.has_ssh_password ? 'Install the WP-hosting agent (full management)' : 'Store an SSH key or password first'}
              onClick={() => { setBootstrapOpen((v) => !v); setDeployOpen(false); }}
            >
              Promote to agent
            </Button>
            </>
          )}
          {server.kind === 'agent' && (
            <Button
              size="sm"
              icon={Globe}
              onClick={() => { setWpOpen((v) => !v); setEditOpen(false); }}
            >
              WP sites
            </Button>
          )}
          <Button
            size="sm"
            icon={Activity}
            disabled={!server.has_ssh_key && !server.has_ssh_password}
            title={server.has_ssh_key || server.has_ssh_password ? 'Stats, power & cache cleanup' : 'Store an SSH key or password first (edit server)'}
            onClick={() => { setOpsOpen((v) => !v); setHttpsOpen(false); setProxyOpen(false); setDeployOpen(false); setBootstrapOpen(false); setEditOpen(false); setWpOpen(false); setMailOpen(false); }}
          >
            Ops
          </Button>
          <Button
            size="sm"
            icon={Mail}
            disabled={!server.has_ssh_key && !server.has_ssh_password}
            title={server.has_ssh_key || server.has_ssh_password ? 'Set up a mail server' : 'Store an SSH key or password first (edit server)'}
            onClick={() => { setMailOpen((v) => !v); setHttpsOpen(false); setProxyOpen(false); setOpsOpen(false); setDeployOpen(false); setBootstrapOpen(false); setEditOpen(false); setWpOpen(false); }}
          >
            Mail
          </Button>
          <Button
            size="sm"
            icon={Network}
            disabled={!server.has_ssh_key && !server.has_ssh_password}
            title={server.has_ssh_key || server.has_ssh_password ? 'SOCKS5 + HTTP proxy exiting on this server’s IP' : 'Store an SSH key or password first (edit server)'}
            onClick={() => { setProxyOpen((v) => !v); setHttpsOpen(false); setMailOpen(false); setOpsOpen(false); setDeployOpen(false); setBootstrapOpen(false); setEditOpen(false); setWpOpen(false); }}
          >
            Proxy
          </Button>
          <Button
            size="sm"
            icon={Lock}
            disabled={!server.has_ssh_key && !server.has_ssh_password}
            title={server.has_ssh_key || server.has_ssh_password ? 'Let’s Encrypt for many domains + HTTP → HTTPS 301 (Apache)' : 'Store an SSH key or password first (edit server)'}
            onClick={() => { setHttpsOpen((v) => !v); setProxyOpen(false); setMailOpen(false); setOpsOpen(false); setDeployOpen(false); setBootstrapOpen(false); setEditOpen(false); setWpOpen(false); }}
          >
            HTTPS
          </Button>
          <Button
            size="sm"
            icon={busy ? RefreshCw : Wifi}
            className={busy ? '[&>svg]:animate-spin' : ''}
            disabled={busy}
            onClick={handleTest}
          >
            Test
          </Button>
          <Button
            size="sm"
            icon={Pencil}
            disabled={busy}
            onClick={() => { setEditOpen((v) => !v); setDeployOpen(false); setBootstrapOpen(false); }}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="danger"
            icon={Trash2}
            disabled={busy}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </TD>
    </TR>
    {deployOpen && (
      <tr className="bg-s-2">
        <td colSpan={7} className="border-b border-bd px-5 py-4">
          <DeployStaticForm server={server} onDone={onChanged} />
        </td>
      </tr>
    )}
    {bootstrapOpen && (
      <tr className="bg-s-2">
        <td colSpan={7} className="border-b border-bd px-5 py-4">
          <BootstrapAgentPanel server={server} onDone={onChanged} />
        </td>
      </tr>
    )}
    {editOpen && (
      <tr className="bg-s-2">
        <td colSpan={7} className="border-b border-bd px-5 py-4">
          <EditServerForm server={server} onDone={() => { setEditOpen(false); onChanged(); }} />
        </td>
      </tr>
    )}
    {wpOpen && (
      <tr className="bg-s-2">
        <td colSpan={7} className="border-b border-bd px-5 py-4">
          <AgentWpPanel server={server} />
        </td>
      </tr>
    )}
    {opsOpen && (
      <tr className="bg-s-2">
        <td colSpan={7} className="border-b border-bd px-5 py-4">
          <OpsPanel server={server} />
        </td>
      </tr>
    )}
    {mailOpen && (
      <tr className="bg-s-2">
        <td colSpan={7} className="border-b border-bd px-5 py-4">
          <MailPanel server={server} />
        </td>
      </tr>
    )}
    {proxyOpen && (
      <tr className="bg-s-2">
        <td colSpan={7} className="border-b border-bd px-5 py-4">
          <ServerProxyPanel server={server} />
        </td>
      </tr>
    )}
    {httpsOpen && (
      <tr className="bg-s-2">
        <td colSpan={7} className="border-b border-bd px-5 py-4">
          <ServerHttpsPanel server={server} />
        </td>
      </tr>
    )}
    </>
  );
}

// ─── Mail Server Setup panel (BUILD-QUEUE §6, Phase A) ──────────────────────

/**
 * One preflight / port-check result. `warn` outranks nothing — a check is either
 * passing, a note, or broken, matching §3's three states. The icon changes with
 * the state as well as the colour, so this reads in greyscale (§8).
 */
function CheckRow({ ok, label, detail, warn }) {
  const Icon = ok ? CheckCircle2 : warn ? Activity : XCircle;
  const tone = ok ? 'text-ok' : warn ? 'text-warn' : 'text-err';
  return (
    <div className="flex items-start gap-2 text-[13px]">
      <Icon className={`mt-0.5 w-4 h-4 shrink-0 ${tone}`} />
      <div className="min-w-0">
        <span className="text-t1">{label}</span>
        {detail && <span className="text-t3"> — {detail}</span>}
      </div>
    </div>
  );
}

function MailPanel({ server }) {
  const [status, setStatus] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [pfLoading, setPfLoading] = useState(false);
  const [pfError, setPfError] = useState(null);
  const [hostname, setHostname] = useState('');
  const [installing, setInstalling] = useState(false);
  const [installLog, setInstallLog] = useState('');
  const [installOutcome, setInstallOutcome] = useState(null);
  const [p25, setP25] = useState(null);
  const [p25Loading, setP25Loading] = useState(false);
  const [p25Error, setP25Error] = useState(null);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const data = await api(`/api/servers/${server.id}/mail`);
      setStatus(data.mail);
      if (data.mail?.mail_hostname && !hostname) setHostname(data.mail.mail_hostname);
    } catch { /* ignore */ }
  }, [server.id, hostname]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  async function runPort25() {
    setP25Loading(true);
    setP25Error(null);
    setP25(null);
    try {
      const data = await api(`/api/servers/${server.id}/mail/port25`, { method: 'POST' });
      setP25(data.port25);
    } catch (err) {
      setP25Error(err.body?.detail ?? err.message);
    } finally {
      setP25Loading(false);
    }
  }

  async function runPreflight() {
    setPfLoading(true);
    setPfError(null);
    setPreflight(null);
    try {
      const data = await api(`/api/servers/${server.id}/mail/preflight`, { method: 'POST' });
      setPreflight(data.preflight);
      if (data.preflight.public_ip && !hostname) setHostname(`mail.`);
    } catch (err) {
      setPfError(err.body?.detail ?? err.message);
    } finally {
      setPfLoading(false);
    }
  }

  async function startInstall() {
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(hostname.trim())) {
      window.alert('Enter a valid mail hostname, e.g. mail.example.com');
      return;
    }
    if (!window.confirm(
      `Install the mail stack (Postfix + Dovecot + OpenDKIM + certbot + fail2ban) on "${server.name}" with hostname ${hostname.trim()}?\n\nThis modifies system services on the target. Safe to re-run.`,
    )) return;
    setInstalling(true);
    setInstallLog('');
    setInstallOutcome(null);
    try {
      const r = await api(`/api/servers/${server.id}/mail/install`, {
        method: 'POST',
        body: { mail_hostname: hostname.trim() },
      });
      pollRef.current = setInterval(async () => {
        try {
          const p = await api(`/api/servers/${server.id}/mail/install/${r.job_id}`);
          setInstallLog(p.log ?? '');
          if (p.job.status === 'done' || p.job.status === 'failed') {
            clearInterval(pollRef.current);
            setInstalling(false);
            setInstallOutcome(p.job.status);
            loadStatus();
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (err) {
      setInstalling(false);
      window.alert(`Install failed to start: ${err.body?.detail ?? err.message}`);
    }
  }

  const statusTone = {
    installed: 'bg-s-green text-[#046B57]',
    installing: 'bg-warn-bg text-[#8B5206]',
    failed: 'bg-err-bg text-err',
    new: 'bg-s-3 text-t2',
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-[13.5px] font-semibold text-t1">
        <Mail className="w-4 h-4 text-ic" />
        Mail server — {server.name}
        {status && (
          <span
            className={[
              'inline-flex items-center gap-1.5 rounded-xl px-[9px] py-1 text-[11.5px] font-medium',
              statusTone[status.status] ?? statusTone.new,
            ].join(' ')}
          >
            <i className="block w-1.5 h-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
            {status.status}{' '}
            {status.mail_hostname && (
              <span className="font-mono font-normal">
                {'· '}
                {status.mail_hostname}
              </span>
            )}
          </span>
        )}
      </div>

      {/* Quick port-25 check */}
      <Card className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="text-[13.5px] font-medium text-t1">Quick port 25 check</span>
            <p className="text-[12.5px] text-t3">
              Can this server send &amp; receive mail on port 25? (30s)
            </p>
          </div>
          <Button
            size="sm"
            icon={p25Loading ? Loader2 : Wifi}
            className={p25Loading ? '[&>svg]:animate-spin' : ''}
            onClick={runPort25}
            disabled={p25Loading}
          >
            Check port 25
          </Button>
        </div>
        {p25Error && <Banner compact kind="error" title="Check failed">{p25Error}</Banner>}
        {p25 && (
          <div className="space-y-1.5">
            <CheckRow
              ok={p25.outbound === 'open'}
              label="Outbound — sending to other servers"
              detail={p25.outbound === 'open'
                ? 'open ✓ this server can send mail out'
                : 'BLOCKED by the host — server-to-server sending won’t work. Ask the provider to unblock port 25, or use a smarthost.'} />
            <CheckRow
              ok={p25.inbound === 'listening' || p25.inbound === 'reachable'}
              warn={p25.inbound === 'reachable'}
              label="Inbound — receiving from other servers"
              detail={p25.inbound === 'listening'
                ? 'reachable ✓ a mail server is already answering on :25'
                : p25.inbound === 'reachable'
                  ? 'network path open ✓ (nothing listening yet — normal before install)'
                  : 'no route in — the firewall likely blocks inbound :25 (receiving won’t work until opened)'} />
          </div>
        )}
      </Card>

      {/* Preflight */}
      <Card className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13.5px] font-medium text-t1">1. Full preflight checks</span>
          <Button
            size="sm"
            icon={pfLoading ? Loader2 : RefreshCw}
            className={pfLoading ? '[&>svg]:animate-spin' : ''}
            onClick={runPreflight}
            disabled={pfLoading}
          >
            Run preflight
          </Button>
        </div>
        {pfError && <Banner compact kind="error" title="Preflight failed">{pfError}</Banner>}
        {preflight && (
          <div className="space-y-1.5">
            <CheckRow ok={preflight.os.supported} label={`OS: ${preflight.os.id} ${preflight.os.version}`}
              detail={preflight.os.supported ? 'supported' : 'not Ubuntu/Debian — install may fail'} warn={!preflight.os.supported} />
            {['25', '587', '465', '993'].map((p) => (
              <CheckRow key={p} ok={preflight.ports[p] === 'yes'} warn={preflight.ports[p] === 'unknown'}
                label={`Port ${p} free`}
                detail={preflight.ports[p] === 'yes' ? 'available' : preflight.ports[p] === 'no' ? 'already in use (existing service?)' : 'could not check'} />
            ))}
            <CheckRow ok={preflight.outbound_25 === 'open'} warn={preflight.outbound_25 === 'unknown'}
              label="Outbound port 25 (server-to-server sending)"
              detail={preflight.outbound_25 === 'open'
                ? 'open — sending will work'
                : preflight.outbound_25 === 'blocked'
                  ? 'BLOCKED by the host — receiving will work, but this server cannot send to other mail servers. Ask the provider to unblock, or use a smarthost.'
                  : 'could not determine'} />
            {preflight.mail_stack_present && (
              <CheckRow ok warn label="A mail stack is already present" detail="re-running install will converge its config" />
            )}
            {preflight.public_ip && (
              <p className="font-mono text-[12px] text-t3">public IP: {preflight.public_ip}</p>
            )}
          </div>
        )}
      </Card>

      {/* Install */}
      <Card className="space-y-3 p-4">
        <span className="text-[13.5px] font-medium text-t1">2. Install the mail stack</span>
        <div className="flex flex-wrap items-start gap-2">
          <Input
            label="Mail hostname"
            mono
            className="w-64"
            type="text"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            placeholder="mail.example.com"
          />
          <Button
            variant="primary"
            icon={installing ? Loader2 : Mail}
            className={['mt-[26px]', installing ? '[&>svg]:animate-spin' : ''].join(' ')}
            onClick={startInstall}
            disabled={installing || !preflight || !preflight.can_install}
            title={!preflight ? 'Run preflight first' : !preflight.can_install ? 'OS not supported' : undefined}
          >
            {status?.status === 'installed' ? 'Re-run install' : 'Install'}
          </Button>
          {installOutcome && (
            <span
              className={[
                'mt-[32px] text-[13px] font-medium',
                installOutcome === 'done' ? 'text-ok' : 'text-err',
              ].join(' ')}
            >
              {installOutcome === 'done' ? '✓ installed' : '✗ failed — see log'}
            </span>
          )}
        </div>
        {(installLog || installing) && (
          /* Install log stays a dark pane — it is literal terminal output and
             reads far better against dark. Not a dark-mode improvisation (§9):
             the colours are --color-t1 and --color-s-2, both existing tokens. */
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-ctl bg-t1 p-3 font-mono text-[11px] text-s-2">
            {installLog || 'starting…'}
          </pre>
        )}
        <p className="text-[12px] text-t3">
          The stack authenticates senders (SASL) — it is never an open relay.
        </p>
      </Card>

      {/* Domains (Phase B) — only once the stack is installed */}
      {status?.status === 'installed' && (
        <MailDomains server={server} mailHostname={status.mail_hostname} />
      )}

      {/* Mailboxes (Phase C) */}
      {status?.status === 'installed' && (
        <MailMailboxes server={server} mailHostname={status.mail_hostname} />
      )}

      {/* Verify & operate (Phase D) */}
      {status?.status === 'installed' && (
        <MailOperate server={server} mailHostname={status.mail_hostname} />
      )}
    </div>
  );
}

// ─── Verify & operate (§6 Phase D) ──────────────────────────────────────────

/** systemd unit state — the word is always next to the dot (§8). */
function SvcDot({ state }) {
  const ok = state === 'active';
  return (
    <span className={`inline-flex items-center gap-1.5 text-[12px] ${ok ? 'text-ok' : 'text-err'}`}>
      <span className="block w-1.5 h-1.5 shrink-0 rounded-full bg-current" />
      {state}
    </span>
  );
}

function MailOperate({ server, mailHostname }) {
  const [status, setStatus] = useState(null);
  const [statusErr, setStatusErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selfTest, setSelfTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const [queue, setQueue] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sendResult, setSendResult] = useState(null);
  const [sending, setSending] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setStatusErr(null);
    try {
      const [st, mb] = await Promise.all([
        api(`/api/servers/${server.id}/mail/status`),
        api(`/api/servers/${server.id}/mail/mailboxes`),
      ]);
      setStatus(st.status);
      setBoxes(mb.mailboxes.map((b) => b.address));
      if (!from && mb.mailboxes.length) { setFrom(mb.mailboxes[0].address); setTo(mb.mailboxes[0].address); }
    } catch (err) {
      setStatusErr(err.body?.detail ?? err.message);
    } finally {
      setLoading(false);
    }
  }, [server.id, from]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function runSelfTest() {
    setTesting(true);
    setSelfTest(null);
    try {
      const d = await api(`/api/servers/${server.id}/mail/self-test`, { method: 'POST' });
      setSelfTest(d.result);
    } catch (err) {
      window.alert(`Self-test failed: ${err.body?.detail ?? err.message}`);
    } finally {
      setTesting(false);
    }
  }

  async function loadQueue() {
    try { setQueue(await api(`/api/servers/${server.id}/mail/queue`)); }
    catch (err) { window.alert(`Queue read failed: ${err.body?.detail ?? err.message}`); }
  }
  async function flushQueue() {
    try { await api(`/api/servers/${server.id}/mail/queue/flush`, { method: 'POST' }); loadQueue(); }
    catch (err) { window.alert(`Flush failed: ${err.body?.detail ?? err.message}`); }
  }

  async function send() {
    if (!from || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(to)) { window.alert('Pick a from mailbox and a valid recipient'); return; }
    setSending(true);
    setSendResult(null);
    try {
      const d = await api(`/api/servers/${server.id}/mail/test-send`, { method: 'POST', body: { from, to } });
      setSendResult(d.result);
    } catch (err) {
      setSendResult({ error: err.body?.detail ?? err.message });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="bg-white border border-bd rounded-md p-4 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-t1">5. Verify &amp; operate</span>
        <Button size="sm" icon={loading ? Loader2 : RefreshCw} className={loading ? '[&>svg]:animate-spin' : ''} onClick={loadStatus} disabled={loading}>
          Refresh status
        </Button>
      </div>

      {statusErr && <Banner compact kind="error" title="Couldn't read mail status">{statusErr}</Banner>}

      {/* Status card */}
      {status && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="border border-bd rounded-md p-3 space-y-1">
            <div className="text-xs font-semibold text-t3 uppercase mb-1">Services</div>
            <div className="flex justify-between text-sm"><span>Postfix</span><SvcDot state={status.services.postfix} /></div>
            <div className="flex justify-between text-sm"><span>Dovecot</span><SvcDot state={status.services.dovecot} /></div>
            <div className="flex justify-between text-sm"><span>OpenDKIM</span><SvcDot state={status.services.opendkim} /></div>
            <div className="flex justify-between text-sm"><span>fail2ban</span><SvcDot state={status.services.fail2ban} /></div>
          </div>
          <div className="border border-bd rounded-md p-3 space-y-1 text-sm">
            <div className="text-xs font-semibold text-t3 uppercase mb-1">Health</div>
            <div className="flex justify-between">
              <span>TLS cert</span>
              {/* A self-signed stopgap also has ~365 days left — days alone would read as healthy. */}
              <span className={status.cert.self_signed || (status.cert.days_left != null && status.cert.days_left < 15) ? 'text-warn' : 'text-t2'}>
                {status.cert.self_signed && 'self-signed — mail clients will warn · '}
                {status.cert.days_left != null ? `${status.cert.days_left} days left` : 'unknown'}
              </span>
            </div>
            <div className="flex justify-between"><span>Mail queue</span><span className={status.queue > 0 ? 'text-warn' : 'text-t2'}>{status.queue}</span></div>
            <div className="flex justify-between">
              <span>Ports</span>
              <span className="font-mono text-xs">
                {['25', '587', '465', '993'].map((p) => (
                  <span key={p} className={status.listeners[p] ? 'text-ok' : 'text-err'}>{p} </span>
                ))}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Open-relay self-test */}
      <div className="border border-bd rounded-md p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-t1">Open-relay self-test <span className="text-xs font-normal text-t3">(must reject unauthenticated relay)</span></span>
          <Button size="sm" icon={testing ? Loader2 : Wifi} className={testing ? '[&>svg]:animate-spin' : ''} onClick={runSelfTest} disabled={testing}>
            Run self-test
          </Button>
        </div>
        {selfTest && (
          <div className="space-y-1">
            {/* Only 'closed' is green. 'unknown' means a probe never got an answer to the relay question — never a pass. */}
            <div className={`text-sm font-medium ${{ open_relay: 'text-err', closed: 'text-ok' }[selfTest.relay.verdict] ?? 'text-warn'}`}>
              {selfTest.relay.verdict === 'open_relay' && '⚠️ OPEN RELAY — unauthenticated relay was ACCEPTED. Do not use this server for mail.'}
              {selfTest.relay.verdict === 'closed' && '✓ Not an open relay — from outside, unauthenticated relay is rejected on 25, 587 and 465.'}
              {selfTest.relay.verdict === 'unknown' && '⚠️ Could not determine — at least one probe never got an answer to the relay question. This is not a pass.'}
            </div>
            <div className="text-xs text-t3">
              Probed from the panel, as an outside stranger, relaying to <span className="font-mono">{selfTest.relay.rcpt}</span> (stops before DATA — nothing is sent).
            </div>
            <ul className="text-xs text-t3 space-y-0.5">
              {selfTest.relay.probes.map((p) => (
                <li key={p.port}>
                  port {p.port}{p.tls ? ' (TLS)' : ''}:{' '}
                  <span className={{ rejected: 'text-ok', accepted: 'text-err' }[p.verdict] ?? 'text-warn'}>{p.verdict}</span>
                  {p.reply && <> — <span className="font-mono">{p.reply}</span></>}
                  {p.detail && <> — {p.detail}{p.verdict === 'unknown' && ` (at ${p.stage})`}</>}
                </li>
              ))}
            </ul>
            {selfTest.dkim.map((d) => (
              <div key={d.domain} className="text-xs text-t3">
                DKIM key <span className="font-mono">{d.selector}._domainkey.{d.domain}</span>:{' '}
                <span className={d.ok ? 'text-ok' : 'text-warn'}>{d.ok ? 'published & matches ✓' : d.detail}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Test send */}
      <div className="border border-bd rounded-md p-3 space-y-2">
        <span className="text-sm font-medium text-t1">Send a test email <span className="text-xs font-normal text-t3">(authenticated via 587, checks DKIM signing)</span></span>
        <div className="flex flex-wrap items-start gap-2">
          <Select label="From (mailbox)" value={from} onChange={(e) => setFrom(e.target.value)}>
            {boxes.length === 0 && <option value="">no mailbox</option>}
            {boxes.map((b) => <option key={b} value={b}>{b}</option>)}
          </Select>
          <Input
            label="To"
            mono
            className="w-56"
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="recipient@example.com"
          />
          <Button variant="primary" className={['mt-[26px]', sending ? '[&>svg]:animate-spin' : ''].join(' ')} icon={sending ? Loader2 : Mail} onClick={send} disabled={sending || boxes.length === 0}>
            Send test
          </Button>
        </div>
        <p className="text-xs text-t3">Tip: send to the same mailbox to check DKIM signing locally, or to an external address (e.g. a Gmail you own) to confirm end-to-end delivery.</p>
        {sendResult && (
          sendResult.error ? (
            <Banner compact kind="error" title="Send failed">{sendResult.error}</Banner>
          ) : (
            <Banner compact kind="success" title="Sent">
              Accepted for {sendResult.accepted.join(', ') || '—'}.
              {sendResult.dkim?.checked === 'local_copy' && sendResult.dkim.signed && <> Delivered copy is <span className="font-medium">DKIM-signed ✓</span> <span className="font-mono">d={sendResult.dkim.d} s={sendResult.dkim.s}</span>.</>}
              {sendResult.dkim?.checked === 'local_copy' && !sendResult.dkim.signed && <> <span className="text-err">The delivered copy has no DKIM-Signature — check OpenDKIM.</span></>}
              {sendResult.dkim?.checked === 'external' && <> DKIM not checked here — the recipient is not on this server. Check the received headers (Gmail: Show original).</>}
              {sendResult.dkim?.checked === 'not_found' && <> <span className="text-warn">DKIM not checked — the message had not reached the mailbox after 2.5s.</span></>}
              {sendResult.dkim?.checked === 'error' && <> <span className="text-warn">DKIM not checked — reading the delivered copy failed.</span></>}
            </Banner>
          )
        )}
      </div>

      {/* Queue */}
      <div className="border border-bd rounded-md p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-t1">Mail queue</span>
          <div className="flex gap-1.5">
            <Button size="sm" icon={RefreshCw} onClick={loadQueue}>View</Button>
            <Button size="sm" onClick={flushQueue}>Flush</Button>
          </div>
        </div>
        {queue && (
          <>
            <div className="text-xs text-t3">{queue.count} message(s) queued</div>
            {queue.count > 0 && (
              <pre className="bg-t1 text-s-2 text-[11px] font-mono rounded-md p-2 max-h-40 overflow-auto whitespace-pre-wrap">{queue.listing}</pre>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Mail domains + DKIM + DNS checklist (§6 Phase B) ───────────────────────

// The checklist is read live per domain: one action per record, decided from
// what the domain already publishes (backend/lib/mailDnsChecklist.js).
const CHECKLIST_ACTIONS = {
  ADD: { label: 'ADD', className: 'bg-s-2 text-t1 border-bd' },
  EDIT: { label: 'EDIT', className: 'bg-warn-bg text-[#8B5206] border-warn-bd' },
  KEEP: { label: 'KEEP', className: 'bg-s-green text-ok border-bd' },
  CONFLICT: { label: 'CONFLICT', className: 'bg-err-bg text-err border-err-bd' },
  UNKNOWN: { label: 'CANNOT TELL', className: 'bg-warn-bg text-[#8B5206] border-warn-bd' },
};

const PROPOSED_LABELS = { ADD: 'set', EDIT: 'new value', CONFLICT: "panel's value" };

const CHECKLIST_SUMMARY = {
  ok: { label: 'DNS all green', className: 'bg-s-green text-ok border-bd' },
  fail: { label: 'DNS incomplete', className: 'bg-warn-bg text-warn border-warn-bd' },
  unknown: { label: 'DNS: cannot tell', className: 'bg-warn-bg text-warn border-warn-bd' },
};

function VerdictIcon({ status }) {
  if (status === 'ok') return <CheckCircle2 className="w-4 h-4 text-ok mt-0.5 shrink-0" aria-label="OK" />;
  if (status === 'unknown') return <CircleHelp className="w-4 h-4 text-warn mt-0.5 shrink-0" aria-label="Cannot tell" />;
  return <XCircle className="w-4 h-4 text-err mt-0.5 shrink-0" aria-label="Not OK" />;
}

function ValueLine({ label, values, copyLabel }) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-20 shrink-0 text-t3">{label}</span>
      <div className="flex-1 min-w-0 font-mono text-t2 break-all space-y-0.5">
        {values.map((v, i) => <div key={i}>{v}</div>)}
      </div>
      {copyLabel && (
        <button
          type="button"
          onClick={() => navigator.clipboard?.writeText(values[0])}
          title={copyLabel}
          aria-label={copyLabel}
          className="text-t3 hover:text-t2"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function ChecklistRow({ rec, applyInfo, applyResult, applying, onApply }) {
  const action = CHECKLIST_ACTIONS[rec.action];
  const canWrite = rec.action === 'ADD' || rec.action === 'EDIT';
  const proposedLabel = PROPOSED_LABELS[rec.action] ?? 'set rDNS to';
  return (
    <div className="flex items-start gap-2 text-xs border-b border-bd last:border-0 py-2">
      <VerdictIcon status={rec.status} />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          {action && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${action.className}`}>{action.label}</span>}
          <span className="font-mono font-semibold text-t3">{rec.type}</span>
          <span className="font-mono text-t2 break-all">{rec.name}</span>
          {canWrite && applyInfo?.can_apply && (
            <Button size="sm" variant={applyInfo.needs_confirm ? 'danger' : 'secondary'} icon={applying ? Loader2 : undefined}
              className={`ml-auto ${applying ? '[&>svg]:animate-spin' : ''}`} disabled={!!applying} onClick={() => onApply(rec)}>
              Apply
            </Button>
          )}
        </div>
        {canWrite && applyInfo && !applyInfo.can_apply && applyInfo.detail && (
          <div className="text-[11px] text-t3">Apply: {applyInfo.detail}</div>
        )}
        {applyResult && (
          <div className={`text-[11px] ${applyResult.ok ? 'text-ok' : 'text-err'}`}>
            {applyResult.ok ? `Written to ${applyResult.zone} and read back — public DNS can take a few minutes to show it.` : `Not applied: ${applyResult.detail ?? applyResult.error}`}
          </div>
        )}
        {rec.current.length > 0 && <ValueLine label="now" values={rec.current} />}
        {rec.proposed && (
          <ValueLine label={proposedLabel} values={[rec.proposed]} copyLabel={`Copy the ${rec.type} value for ${rec.name}`} />
        )}
        <div className="text-t3">{rec.detail}</div>
        {rec.warnings.map((w, i) => (
          <div
            key={i}
            className={`flex items-start gap-1.5 px-2 py-1.5 border-l-4 ${w.level === 'danger' ? 'bg-err-bg border-l-err text-err font-medium' : 'bg-warn-bg border-l-warn text-[#8B5206]'}`}
          >
            <TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{w.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DomainCard({ server, domain, mailHostname, onRemoved }) {
  const [checklist, setChecklist] = useState(null);
  const [applyInfo, setApplyInfo] = useState(null); // { fingerprint, rows: { key: { can_apply, detail, … } } }
  const [applying, setApplying] = useState(null);
  const [applyResults, setApplyResults] = useState({});
  const [applyNote, setApplyNote] = useState(null);
  const [checkError, setCheckError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [open, setOpen] = useState(true);

  const checkDns = useCallback(async () => {
    setChecking(true);
    try {
      const data = await api(`/api/servers/${server.id}/mail/domains/${domain.domain}/dns`);
      setChecklist(data.records);
      setApplyInfo(data.apply ?? null);
      setCheckError(null);
    } catch (err) {
      setChecklist(null);
      setCheckError(err.body?.detail ?? err.message);
    } finally {
      setChecking(false);
    }
  }, [server.id, domain.domain]);

  useEffect(() => { checkDns(); }, [checkDns]);

  async function remove() {
    if (!window.confirm(`Remove mail domain "${domain.domain}"?\n\nIt stops accepting & signing mail. Type nothing — just confirm.`)) return;
    try {
      await api(`/api/servers/${server.id}/mail/domains/${domain.domain}`, {
        method: 'DELETE',
        body: { confirm_domain: domain.domain },
      });
      onRemoved();
    } catch (err) {
      window.alert(`Remove failed: ${err.body?.detail ?? err.message}`);
    }
  }

  // Apply = write the row to the DNS host (Spaceship). The server re-reads the checklist
  // and refuses if it no longer matches what is shown here (fingerprint).
  async function apply(rec) {
    const info = applyInfo?.rows?.[rec.key];
    if (info?.needs_confirm && !window.confirm(
      `Change the MX of ${domain.domain}?\n\n${rec.warnings.map((w) => w.text).join('\n')}\n\nEvery current MX record is replaced by: ${rec.proposed}`,
    )) return;
    setApplying(rec.key);
    setApplyNote(null);
    try {
      const r = await api(`/api/servers/${server.id}/mail/domains/${domain.domain}/dns/apply`, {
        method: 'POST',
        body: { keys: [rec.key], fingerprint: applyInfo.fingerprint, confirm_mx: info?.needs_confirm === true },
      });
      setApplyResults((prev) => ({ ...prev, ...Object.fromEntries(r.results.map((x) => [x.key, x])) }));
      checkDns();
    } catch (err) {
      if (err.status === 409 && err.body?.records) {
        setChecklist(err.body.records);
        setApplyInfo(err.body.apply ?? null);
        setApplyNote('DNS changed since the checklist was read — the rows above are fresh; check them, then Apply again.');
      } else if (err.body?.results) {
        setApplyResults((prev) => ({ ...prev, ...Object.fromEntries(err.body.results.map((x) => [x.key, x])) }));
        checkDns();
      } else {
        setApplyNote(`Apply failed: ${err.body?.detail ?? err.message}`);
      }
    } finally {
      setApplying(null);
    }
  }

  // 'ok' only when every row proved it; any unproven row keeps the badge off green.
  const summary = checklist && (
    checklist.some((r) => r.status === 'fail') ? 'fail'
      : checklist.some((r) => r.status !== 'ok') ? 'unknown'
        : 'ok');
  const ptr = checklist?.find((r) => r.key === 'ptr');

  return (
    <div className="border border-bd rounded-md">
      <div className="flex items-center justify-between px-3 py-2 bg-s-2 rounded-t-md">
        <button type="button" onClick={() => setOpen((v) => !v)} className="text-sm font-medium text-t1 flex items-center gap-2">
          <Globe className="w-4 h-4 text-t3" /> {domain.domain}
          {summary && (
            <span className={`text-xs px-1.5 py-0.5 rounded border ${CHECKLIST_SUMMARY[summary].className}`}>
              {CHECKLIST_SUMMARY[summary].label}
            </span>
          )}
        </button>
        <div className="flex items-center gap-1.5">
          <Button size="sm" icon={checking ? Loader2 : RefreshCw} className={checking ? '[&>svg]:animate-spin' : ''} onClick={checkDns} disabled={checking}>
            Check DNS
          </Button>
          <Button size="sm" variant="danger" icon={Trash2} onClick={remove}>Remove</Button>
        </div>
      </div>
      {open && (
        <div className="p-3 space-y-2">
          <div className="text-xs font-semibold text-t3 uppercase">DNS checklist — read live from {domain.domain}</div>
          {checkError && <Banner compact kind="error" title="DNS check failed">{checkError}</Banner>}
          {!checklist && !checkError && <p className="text-xs text-t3">Reading live DNS…</p>}
          {checklist && (
            <>
              <p className="text-[11px] text-t3">
                One action per record, from what the domain publishes now. KEEP: touch nothing. EDIT: replace the
                current value with the new one. CONFLICT: resolve by hand. CANNOT TELL: re-check before changing anything.
              </p>
              <div className="bg-white">
                {checklist.map((rec) => (
                  <ChecklistRow key={rec.key} rec={rec} applyInfo={applyInfo?.rows?.[rec.key]} applyResult={applyResults[rec.key]}
                    applying={applying === rec.key} onApply={apply} />
                ))}
              </div>
              {applyNote && <Banner compact kind="note" title="DNS">{applyNote}</Banner>}
              {ptr && ptr.status !== 'ok' && (
                <p className="text-[11px] text-warn">
                  PTR is set at the hosting provider, not your DNS host. On Linode: Network → the IP → edit rDNS → {mailHostname}.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SettingsBlock({ settings }) {
  if (!settings) return null;
  const line = (label, v) => (
    <div className="flex gap-2">
      <span className="w-28 text-t3">{label}</span>
      <span className="font-mono text-t1">{v}</span>
    </div>
  );
  return (
    <div className="text-xs bg-s-2 border border-bd rounded-md p-3 space-y-2">
      <div className="space-y-0.5">
        <div className="font-semibold text-t2 uppercase text-[10px]">Incoming (IMAP)</div>
        {line('Host', settings.incoming.host)}
        {line('Port', `${settings.incoming.port} (${settings.incoming.security})`)}
      </div>
      <div className="space-y-0.5">
        <div className="font-semibold text-t2 uppercase text-[10px]">Outgoing (SMTP)</div>
        {line('Host', settings.outgoing_starttls.host)}
        {line('Port', `587 (STARTTLS) or 465 (SSL/TLS)`)}
      </div>
      {line('Username', settings.username)}
      <p className="text-[11px] text-t3">{settings.note}</p>
    </div>
  );
}

function PasswordReveal({ password }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <code className="font-mono bg-white border border-warn-bd rounded-ctl px-2 py-1">{password}</code>
      <Button size="sm" icon={Copy} onClick={() => navigator.clipboard?.writeText(password)}>Copy</Button>
    </div>
  );
}

function MailboxRow({ server, box, mailHostname, onChanged }) {
  const [reveal, setReveal] = useState(null); // { password, settings }
  const [busy, setBusy] = useState(false);

  async function showCreds() {
    setBusy(true);
    try {
      const data = await api(`/api/servers/${server.id}/mail/mailboxes/${encodeURIComponent(box.address)}/credentials`);
      setReveal({ password: data.password, settings: data.settings });
    } catch (err) {
      window.alert(`Reveal failed: ${err.body?.detail ?? err.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function resetPw() {
    if (!window.confirm(`Reset the password for ${box.address}? The old one stops working immediately.`)) return;
    setBusy(true);
    try {
      const data = await api(`/api/servers/${server.id}/mail/mailboxes/${encodeURIComponent(box.address)}/reset-password`, { method: 'POST' });
      setReveal({ password: data.password, settings: data.settings, fresh: true });
    } catch (err) {
      window.alert(`Reset failed: ${err.body?.detail ?? err.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function resync() {
    setBusy(true);
    try {
      await api(`/api/servers/${server.id}/mail/mailboxes/${encodeURIComponent(box.address)}/resync`, { method: 'POST' });
      window.alert(`${box.address} re-synced — inbound delivery should work now. Password unchanged.`);
    } catch (err) {
      window.alert(`Re-sync failed: ${err.body?.detail ?? err.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (!window.confirm(`DELETE mailbox ${box.address}?\n\nThis removes the account AND all its stored mail on the server. Irreversible.`)) return;
    setBusy(true);
    try {
      await api(`/api/servers/${server.id}/mail/mailboxes/${encodeURIComponent(box.address)}`, {
        method: 'DELETE', body: { confirm_address: box.address },
      });
      onChanged();
    } catch (err) {
      window.alert(`Delete failed: ${err.body?.detail ?? err.message}`);
      setBusy(false);
    }
  }

  return (
    <div className="border border-bd rounded-md">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-sm font-medium text-t1 font-mono">{box.address}</span>
        <div className="flex items-center gap-1.5">
          <Button size="sm" icon={Mail} onClick={showCreds} disabled={busy}>Settings</Button>
          <Button size="sm" icon={RefreshCw} onClick={resync} disabled={busy}
            title="Re-apply on the server with the same password (fixes inbound delivery)">
            Re-sync
          </Button>
          <Button size="sm" icon={RefreshCw} onClick={resetPw} disabled={busy}>Reset password</Button>
          <Button size="sm" variant="danger" icon={Trash2} onClick={del} disabled={busy}>Delete</Button>
        </div>
      </div>
      {reveal && (
        <div className="px-3 pb-3 space-y-2">
          <div className="space-y-1 border-l-4 border-l-warn bg-warn-bg px-3 py-2">
            <div className="text-xs font-semibold text-[#8B5206]">
              {reveal.fresh ? 'New password — copy it now' : 'Password'}
            </div>
            <PasswordReveal password={reveal.password} />
          </div>
          <SettingsBlock settings={reveal.settings} />
          <button type="button" onClick={() => setReveal(null)} className="text-xs text-t3 underline">hide</button>
        </div>
      )}
    </div>
  );
}

function MailMailboxes({ server, mailHostname }) {
  const [boxes, setBoxes] = useState(null);
  const [domains, setDomains] = useState([]);
  const [localPart, setLocalPart] = useState('');
  const [domain, setDomain] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [justCreated, setJustCreated] = useState(null); // { address, password, settings }

  const load = useCallback(async () => {
    try {
      const [mb, dm] = await Promise.all([
        api(`/api/servers/${server.id}/mail/mailboxes`),
        api(`/api/servers/${server.id}/mail/domains`),
      ]);
      setBoxes(mb.mailboxes);
      setDomains(dm.domains.map((d) => d.domain));
      if (!domain && dm.domains.length) setDomain(dm.domains[0].domain);
      setError(null);
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    }
  }, [server.id, domain]);

  useEffect(() => { load(); }, [load]);

  async function create() {
    const lp = localPart.trim().toLowerCase();
    if (!/^[a-z0-9]([a-z0-9._-]{0,62})?$/i.test(lp)) { window.alert('Enter a valid mailbox name (before the @)'); return; }
    if (!domain) { window.alert('Add a mail domain first'); return; }
    setCreating(true);
    setError(null);
    setJustCreated(null);
    try {
      const data = await api(`/api/servers/${server.id}/mail/mailboxes`, {
        method: 'POST', body: { local_part: lp, domain },
      });
      setJustCreated({ address: data.mailbox.address, password: data.password, settings: data.settings });
      setLocalPart('');
      load();
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="bg-white border border-bd rounded-md p-4 space-y-3">
      <span className="text-sm font-medium text-t1">4. Mailboxes</span>

      <div className="flex flex-wrap items-start gap-2">
        <InputGroup label="Mailbox">
          <input
            type="text"
            value={localPart}
            onChange={(e) => setLocalPart(e.target.value)}
            placeholder="you"
            className="w-32 font-mono"
          />
          <span>@</span>
          <select value={domain} onChange={(e) => setDomain(e.target.value)} className="font-mono">
            {domains.length === 0 && <option value="">no domain</option>}
            {domains.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </InputGroup>
        <Button variant="primary" className={['mt-[26px]', creating ? '[&>svg]:animate-spin' : ''].join(' ')} icon={creating ? Loader2 : Plus} onClick={create} disabled={creating || domains.length === 0}>
          Create mailbox
        </Button>
      </div>
      {domains.length === 0 && <p className="text-xs text-warn">Add a mail domain (section 3) before creating mailboxes.</p>}
      {error && <Banner compact kind="error" title="Something failed">{error}</Banner>}

      {justCreated && (
        <div className="space-y-2 border-l-4 border-l-warn bg-warn-bg px-4 py-3">
          <div className="text-sm font-semibold text-[#8B5206]">
            Mailbox {justCreated.address} created — password shown ONCE, copy it now.
          </div>
          <PasswordReveal password={justCreated.password} />
          <SettingsBlock settings={justCreated.settings} />
          <button type="button" onClick={() => setJustCreated(null)} className="text-xs text-warn underline">I saved it — dismiss</button>
        </div>
      )}

      {boxes === null ? (
        <p className="text-sm text-t3">Loading…</p>
      ) : boxes.length === 0 ? (
        <p className="text-sm text-t3">No mailboxes yet.</p>
      ) : (
        <div className="space-y-2">
          {boxes.map((b) => (
            <MailboxRow key={b.address} server={server} box={b} mailHostname={mailHostname} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function existingMailDnsWarning(domain, existing) {
  const lines = [`${domain} already has mail DNS:`, ''];
  if (existing.mx.length) lines.push(`• MX: ${existing.mx.join(', ')}\n  Pointing MX here moves ALL inbound mail for ${domain} to this server.`);
  if (existing.spf.length) lines.push(`• SPF: ${existing.spf.join(' | ')}\n  It must be merged, never replaced — the checklist shows the merged value.`);
  if (existing.dmarc.length) lines.push(`• DMARC: ${existing.dmarc.join(' | ')}\n  Keep it. Never add a second DMARC record.`);
  if (existing.unknown.length) lines.push(`• Could not read: ${existing.unknown.join(', ')} — treat these as possibly present.`);
  lines.push('', 'Add it anyway? This generates the DKIM key and wires the domain on the server; no DNS is changed. The checklist then shows what to add, edit or keep.');
  return lines.join('\n');
}

function MailDomains({ server, mailHostname }) {
  const [domains, setDomains] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api(`/api/servers/${server.id}/mail/domains`);
      setDomains(data.domains);
      setError(null);
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    }
  }, [server.id]);

  useEffect(() => { load(); }, [load]);

  async function add() {
    const d = newDomain.trim().toLowerCase();
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(d)) { window.alert('Enter a valid domain, e.g. example.com'); return; }
    setAdding(true);
    setError(null);
    const url = `/api/servers/${server.id}/mail/domains`;
    try {
      try {
        await api(url, { method: 'POST', body: { domain: d } });
      } catch (err) {
        // The backend refuses a domain that already has mail DNS until confirmed.
        if (err.status !== 409 || err.body?.error !== 'existing_mail_dns') throw err;
        if (!window.confirm(existingMailDnsWarning(d, err.body.existing))) return;
        await api(url, { method: 'POST', body: { domain: d, confirm_existing_dns: true } });
      }
      setNewDomain('');
      load();
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="bg-white border border-bd rounded-md p-4 space-y-3">
      <span className="text-sm font-medium text-t1">3. Mail domains &amp; DKIM</span>
      <div className="flex items-start gap-2">
        <Input
          label="Add a domain"
          mono
          className="w-64"
          type="text"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          placeholder="example.com"
        />
        <Button variant="primary" className={['mt-[26px]', adding ? '[&>svg]:animate-spin' : ''].join(' ')} icon={adding ? Loader2 : Plus} onClick={add} disabled={adding}>
          Add domain
        </Button>
      </div>
      {error && <Banner compact kind="error" title="Something failed">{error}</Banner>}
      {domains === null ? (
        <p className="text-sm text-t3">Loading…</p>
      ) : domains.length === 0 ? (
        <p className="text-sm text-t3">No mail domains yet. Add one to generate its DKIM key and DNS checklist.</p>
      ) : (
        <div className="space-y-2">
          {domains.map((d) => (
            <DomainCard key={d.domain} server={server} domain={d} mailHostname={mailHostname} onRemoved={load} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Ops panel: stats + power + clear cache (BUILD-QUEUE §4) ────────────────

function fmtBytes(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function OpsPanel({ server }) {
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [powerOpts, setPowerOpts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [cacheOpen, setCacheOpen] = useState(false);

  const loadStats = useCallback(async () => {
    setStatsError(null);
    try {
      const data = await api(`/api/servers/${server.id}/ops/stats`);
      setStats(data.stats);
    } catch (err) {
      setStatsError(err.body?.detail ?? err.message);
    }
  }, [server.id]);

  useEffect(() => {
    loadStats();
    api(`/api/servers/${server.id}/ops/power-options`)
      .then(setPowerOpts)
      .catch(() => setPowerOpts({ cloud_match: null, can_start: false, can_stop: true, can_reboot: true }));
  }, [server.id, loadStats]);

  async function handlePower(action) {
    const via = powerOpts?.cloud_match
      ? `via ${powerOpts.cloud_match.provider === 'do' ? 'DigitalOcean' : 'Linode'} (${powerOpts.cloud_match.server_name})`
      : 'via SSH';
    const messages = {
      reboot: `Reboot "${server.name}" (${server.host}) ${via}?\nThe server will be briefly unreachable.`,
      stop: `POWER OFF "${server.name}" (${server.host}) ${via}?\n\n${powerOpts?.cloud_match ? 'You can start it again from here (provider API).' : '⚠️ SSH-only server: it CANNOT be started remotely once off — you need provider console access.'}`,
      start: `Start "${server.name}" ${via}?`,
    };
    if (!window.confirm(messages[action])) return;
    setBusy(true);
    setNotice(null);
    try {
      const r = await api(`/api/servers/${server.id}/ops/power`, { method: 'POST', body: { action } });
      setNotice({ ok: true, text: `${action} issued (${r.via}). Give it a minute, then Test / refresh stats.` });
    } catch (err) {
      setNotice({ ok: false, text: `${action} failed: ${err.body?.detail ?? err.message}` });
    } finally {
      setBusy(false);
    }
  }

  const memPct = stats?.memory
    ? Math.round(((stats.memory.total_bytes - stats.memory.available_bytes) / stats.memory.total_bytes) * 100)
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-t1">
          <Activity className="w-4 h-4 text-ic" />
          Ops — {server.name}
          {powerOpts?.cloud_match && (
            <span className="text-[12px] font-normal text-t3">
              (cloud: {powerOpts.cloud_match.provider === 'do' ? 'DigitalOcean' : 'Linode'} · {powerOpts.cloud_match.server_name})
            </span>
          )}
        </div>
        {/* Power actions: Stop is destructive-adjacent (an SSH-only box cannot be
            started again from here), so it is `danger` per §3. Start and Reboot
            are ordinary. */}
        <div className="flex items-center gap-1.5">
          {powerOpts?.can_start && (
            <Button size="sm" icon={Play} disabled={busy} onClick={() => handlePower('start')}>
              Start
            </Button>
          )}
          {(powerOpts?.can_stop ?? true) && (
            <Button size="sm" variant="danger" icon={Power} disabled={busy} onClick={() => handlePower('stop')}>
              Stop
            </Button>
          )}
          {(powerOpts?.can_reboot ?? true) && (
            <Button size="sm" icon={RotateCw} disabled={busy} onClick={() => handlePower('reboot')}>
              Reboot
            </Button>
          )}
          <Button size="sm" icon={Eraser} onClick={() => setCacheOpen((v) => !v)}>
            Clear cache
          </Button>
          <Button size="sm" icon={RefreshCw} onClick={loadStats}>
            Refresh
          </Button>
        </div>
      </div>

      {notice && (
        <Banner
          compact
          kind={notice.ok ? 'success' : 'error'}
          title={notice.ok ? 'Power action issued' : 'Power action failed'}
        >
          {notice.text}
        </Banner>
      )}

      {cacheOpen && <ClearCachePanel server={server} onDone={loadStats} />}

      {statsError ? (
        <Banner compact kind="error" title="Couldn't read stats">
          {statsError} — nothing was changed on the server; this is a read-only
          SSH snapshot.
        </Banner>
      ) : stats === null ? (
        <p className="text-[13px] text-t3">Reading stats over SSH…</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="p-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.09em] text-t3">Disks</p>
            <div className="space-y-2.5">
              {stats.mounts.map((m) => (
                <div key={m.mount} className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1">
                  <span className="truncate font-mono text-[12.5px] text-t1">{m.mount}</span>
                  <span className="font-mono text-[12px] text-t3">
                    {fmtBytes(m.used_bytes)} / {fmtBytes(m.size_bytes)}
                  </span>
                  <Gauge
                    className="col-span-2"
                    pct={m.use_pct}
                    value={`${m.use_pct}%`}
                  />
                </div>
              ))}
            </div>
          </Card>

          <Card className="space-y-3 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-t3">Memory &amp; load</p>
            {stats.memory && (
              <div>
                <Gauge label="MEMORY" pct={memPct} value={`${memPct}%`} />
                <p className="mt-1 font-mono text-[12px] text-t3">
                  {fmtBytes(stats.memory.total_bytes - stats.memory.available_bytes)} / {fmtBytes(stats.memory.total_bytes)} used
                </p>
              </div>
            )}
            {stats.load && (
              <div className="flex items-center gap-2 text-[12.5px]">
                <span className="w-16 shrink-0 text-[11px] font-semibold tracking-[0.06em] text-t3">LOAD</span>
                <span
                  className={[
                    'font-mono',
                    stats.cores && stats.load[0] > stats.cores ? 'font-semibold text-err' : 'text-t2',
                  ].join(' ')}
                >
                  {stats.load.join(' · ')}
                </span>
                {stats.cores && <span className="text-[12px] text-t3">({stats.cores} cores)</span>}
              </div>
            )}
            {stats.uptime && (
              <div className="flex items-center gap-2 text-[12.5px]">
                <span className="w-16 shrink-0 text-[11px] font-semibold tracking-[0.06em] text-t3">UPTIME</span>
                <span className="text-t2">{stats.uptime}</span>
              </div>
            )}
            <p className="text-[11px] text-t3">
              sampled {new Date(stats.sampled_at).toLocaleTimeString()}
            </p>
          </Card>
        </div>
      )}
    </div>
  );
}

function ClearCachePanel({ server, onDone }) {
  const [steps, setSteps] = useState(null);
  const [checked, setChecked] = useState({});
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api(`/api/servers/${server.id}/ops/cache-steps`)
      .then((d) => {
        setSteps(d.steps);
        setChecked(Object.fromEntries(d.steps.map((s) => [s.key, !!s.default])));
      })
      .catch((err) => setError(err.message));
  }, [server.id]);

  const selectedKeys = steps ? steps.filter((s) => checked[s.key]).map((s) => s.key) : [];

  async function handleRun() {
    if (!selectedKeys.length) return;
    const cmds = steps.filter((s) => checked[s.key]).map((s) => `  ${s.command}`).join('\n');
    if (!window.confirm(`Run cache cleanup on "${server.name}" (${server.host})?\n\nExact commands:\n${cmds}\n\nProceed?`)) return;
    setRunning(true);
    setOutcome(null);
    setError(null);
    try {
      const r = await api(`/api/servers/${server.id}/ops/clear-cache`, {
        method: 'POST',
        body: { steps: selectedKeys },
      });
      setOutcome(r);
      onDone();
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    } finally {
      setRunning(false);
    }
  }

  return (
    <Card className="space-y-3 p-4">
      <p className="text-[13.5px] font-semibold text-t1">
        Clear cache — commands run exactly as shown
      </p>
      {error && <Banner compact kind="error" title="Cleanup failed">{error}</Banner>}
      {steps === null ? (
        <p className="text-[13px] text-t3">Loading…</p>
      ) : (
        <>
          <div className="space-y-2.5">
            {steps.map((s) => (
              <div key={s.key} className="flex items-start gap-2.5">
                <Checkbox
                  className="mt-0.5"
                  checked={!!checked[s.key]}
                  onChange={() => setChecked((c) => ({ ...c, [s.key]: !c[s.key] }))}
                />
                <div className="min-w-0 flex-1 text-[13px]">
                  <span className="font-medium text-t1">{s.label}</span>
                  <span className="text-t3"> — {s.description}</span>
                  {/* The exact command is the point of this panel — it is what the
                      confirm dialog will run, verbatim. Mono, per §2. */}
                  <code className="mt-1 block rounded-ctl border border-bd bg-s-3 px-1.5 py-0.5 font-mono text-[11.5px] text-t2">
                    {s.command}
                  </code>
                </div>
              </div>
            ))}
          </div>
          <Button
            variant="primary"
            icon={running ? Loader2 : Eraser}
            className={running ? '[&>svg]:animate-spin' : ''}
            disabled={running || selectedKeys.length === 0}
            onClick={handleRun}
          >
            Run {selectedKeys.length} step{selectedKeys.length === 1 ? '' : 's'}
          </Button>
        </>
      )}
      {outcome && (
        <div className="space-y-1.5 border-t border-bd pt-3 text-[12.5px]">
          <p className="font-semibold text-t1">
            Done — freed {outcome.freed_bytes != null ? fmtBytes(outcome.freed_bytes) : 'n/a'} on /
          </p>
          {outcome.results.map((r) => (
            <div key={r.step} className="flex items-start gap-2">
              <span className={r.ok ? 'text-ok' : 'text-err'}>{r.ok ? '✓' : '✗'}</span>
              <span className="font-mono text-t1">{r.step}</span>
              {r.output && (
                <span className="whitespace-pre-wrap break-all font-mono text-t3">{r.output}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AgentWpPanel({ server }) {
  const [sites, setSites] = useState(null);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const loadSites = useCallback(async () => {
    try {
      const data = await api(`/api/servers/${server.id}/sites`);
      setSites(data.sites ?? []);
      setError(null);
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    }
  }, [server.id]);

  useEffect(() => { loadSites(); }, [loadSites]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13.5px] font-medium text-t1">
          WordPress sites on {server.name}{' '}
          <span className="font-mono text-[12.5px] text-t3">({server.host})</span>
        </p>
        <Button size="sm" variant="primary" icon={Plus} onClick={() => setShowCreate((v) => !v)}>
          Create WP site
        </Button>
      </div>

      {showCreate && (
        <CreateWpSiteForm server={server} onCreated={() => { setShowCreate(false); loadSites(); }} />
      )}

      {error && <Banner compact kind="error" title="Couldn't list sites">{error}</Banner>}

      {sites === null ? (
        <p className="text-[12.5px] text-t3">Loading…</p>
      ) : sites.length === 0 ? (
        <p className="text-[12.5px] text-t3">No WordPress sites yet on this server.</p>
      ) : (
        <Card>
          <ul className="divide-y divide-bd">
            {sites.map((s) => (
              <li key={s.domain} className="flex items-center justify-between px-3.5 py-2.5">
                <span className="text-[13.5px] font-medium text-t1">{s.domain}</span>
                <span className="text-[12.5px] text-t3">{s.client_name || <Empty />}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function CreateWpSiteForm({ server, onCreated }) {
  const [domain, setDomain] = useState('');
  const [email, setEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | creating | done | failed
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setPhase('creating');
    try {
      const r = await api(`/api/servers/${server.id}/sites`, {
        method: 'POST',
        body: { domain: domain.trim().toLowerCase(), admin_email: email.trim(), client_name: clientName.trim() || undefined },
      });
      pollRef.current = setInterval(async () => {
        try {
          const j = await api(`/api/servers/jobs/${r.job_id}`);
          setJob(j);
          if (j.status === 'done' || j.status === 'failed') {
            clearInterval(pollRef.current);
            setPhase(j.status === 'done' ? 'done' : 'failed');
            if (j.status === 'done') onCreated();
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (err) {
      const b = err.body;
      setError(typeof b === 'string' ? b : (b?.detail || b?.error || err.message));
      setPhase('failed');
    }
  }

  const creds = job?.result?.credentials;

  if (phase === 'done' && creds) {
    return (
      <Card className="space-y-3 p-4">
        <Banner compact kind="success" title={`${job.result.domain} created on ${server.name}`}>
          Credentials are shown once — copy them now. They are also stored
          encrypted in the panel.
        </Banner>
        <CredBlock title="WordPress admin" items={[
          { label: 'Admin URL', value: creds.wp_admin_url, isLink: true },
          { label: 'Username', value: creds.wp_admin_user },
          { label: 'Password', value: creds.wp_admin_pass, sensitive: true },
        ]} />
        <CredBlock title="Database" items={[
          { label: 'Host', value: creds.db_host },
          { label: 'Name', value: creds.db_name },
          { label: 'User', value: creds.db_user },
          { label: 'Password', value: creds.db_pass, sensitive: true },
        ]} />
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex flex-wrap items-start gap-3">
          <Input
            label="Domain"
            mono
            className="w-56"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="client.com"
            required
            disabled={phase === 'creating'}
          />
          <Input
            label="Admin email"
            mono
            className="w-56"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@client.com"
            required
            disabled={phase === 'creating'}
          />
          <Input
            label="Client name (optional)"
            className="w-48"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            disabled={phase === 'creating'}
          />
          <Button type="submit" variant="primary" className="mt-[26px]" disabled={phase === 'creating'}>
            {phase === 'creating' ? 'Creating… (1-2 min)' : 'Create'}
          </Button>
        </div>
        <p className="text-[12.5px] text-t3">
          Point the domain&apos;s DNS A record at{' '}
          <span className="font-mono text-t2">{server.host}</span> before creating, so
          certbot can issue HTTPS.
        </p>
        <FormError>{error}</FormError>
        {phase === 'failed' && job?.error && (
          <FormError>
            {typeof job.error === 'string'
              ? job.error
              : (job.error.error || JSON.stringify(job.error)).toString().slice(0, 300)}
          </FormError>
        )}
      </form>
    </Card>
  );
}

function EditServerForm({ server, onDone }) {
  const [form, setForm] = useState({
    name: server.name,
    host: server.host,
    ssh_port: server.ssh_port,
    ssh_user: server.ssh_user ?? 'root',
    agent_url: server.agent_url ?? '',
    notes: server.notes ?? '',
    ssh_key: '',
    ssh_password: '',
    agent_secret: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // Only send changed plaintext fields; send secrets only if typed.
      const body = {};
      for (const f of ['name', 'host', 'ssh_user', 'agent_url', 'notes']) {
        if (form[f] !== (server[f] ?? '')) body[f] = form[f];
      }
      if (Number(form.ssh_port) !== server.ssh_port) body.ssh_port = Number(form.ssh_port);
      if (form.ssh_key.trim()) body.ssh_key = form.ssh_key;
      if (form.ssh_password.trim()) body.ssh_password = form.ssh_password;
      if (form.agent_secret.trim()) body.agent_secret = form.agent_secret;
      if (Object.keys(body).length === 0) { onDone(); return; }
      await api(`/api/servers/${server.id}`, { method: 'PATCH', body });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-[12.5px] text-t3">
        Edit <span className="font-medium text-t2">{server.name}</span>. Leave the key
        field empty to keep the current one.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input label="Name" value={form.name} onChange={set('name')} />
        <Input label="Host" mono value={form.host} onChange={set('host')} />
        <Input label="SSH port" mono type="number" value={form.ssh_port} onChange={set('ssh_port')} />
        <Input label="SSH user" mono value={form.ssh_user} onChange={set('ssh_user')} />
        {server.kind === 'agent' && (
          <Input label="Agent URL" mono value={form.agent_url} onChange={set('agent_url')} />
        )}
        <Input label="Notes" value={form.notes} onChange={set('notes')} />
      </div>
      <Textarea
        label={`SSH private key ${server.has_ssh_key ? '(stored — leave empty to keep)' : '(none stored)'}`}
        mono
        rows={5}
        value={form.ssh_key}
        onChange={set('ssh_key')}
        placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
      />
      <Input
        label={`SSH password ${server.has_ssh_password ? '(stored — leave empty to keep)' : '(none stored)'}`}
        mono
        type="password"
        autoComplete="off"
        className="sm:max-w-sm"
        value={form.ssh_password}
        onChange={set('ssh_password')}
        placeholder="alternative to the key — stored encrypted"
      />
      <FormError>{error}</FormError>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        <Button type="button" onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}

function BootstrapAgentPanel({ server, onDone }) {
  const [phase, setPhase] = useState('idle'); // idle | running | done | failed
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => () => { clearInterval(pollRef.current); clearInterval(tickRef.current); }, []);

  async function handleStart() {
    if (!window.confirm(
      `Bootstrap "${server.name}" (${server.host}) as a WP-hosting agent?\n\n` +
      'This installs Node 22, Docker, Apache, certbot and the vps1-api agent ' +
      'on the target (3-8 minutes). Intended for fresh Ubuntu servers.',
    )) return;

    setPhase('running');
    setError(null);
    setElapsed(0);
    tickRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);

    try {
      const r = await api(`/api/servers/${server.id}/bootstrap`, { method: 'POST' });
      pollRef.current = setInterval(async () => {
        try {
          const j = await api(`/api/servers/jobs/${r.job_id}`);
          setJob(j);
          if (j.status === 'done' || j.status === 'failed') {
            clearInterval(pollRef.current);
            clearInterval(tickRef.current);
            setPhase(j.status === 'done' ? 'done' : 'failed');
            onDone();
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (err) {
      clearInterval(tickRef.current);
      setError(err.message);
      setPhase('failed');
    }
  }

  const steps = (job?.result ?? job?.error)?.steps ?? [];
  const mins = String(Math.floor(elapsed / 60));
  const secs = String(elapsed % 60).padStart(2, '0');

  return (
    <div className="space-y-3">
      <p className="text-[12.5px] text-t3">
        Promote <span className="font-medium text-t2">{server.name}</span> to a full
        <span className="font-medium text-t2"> WP-hosting agent</span>: installs the
        stack (Node 22 + PM2, Docker, Apache, certbot) + the vps1-api agent, locks
        port <span className="font-mono text-t2">3002</span> to this panel&apos;s IP,
        then registers it here as kind{' '}
        <span className="font-mono text-t2">agent</span>.
      </p>

      {phase === 'idle' && (
        <Button variant="primary" icon={Cpu} onClick={handleStart}>
          Start bootstrap
        </Button>
      )}

      {phase === 'running' && (
        <p className="inline-flex items-center gap-2 text-[13px] text-t2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Bootstrapping… <span className="font-mono">{mins}:{secs}</span> (apt + Docker
          image build take a while — hang tight)
        </p>
      )}

      <FormError>{error}</FormError>

      {steps.length > 0 && <JobSteps steps={steps} />}

      {phase === 'done' && (
        <Banner compact kind="success" title="Agent installed">
          {server.name} is now a WP-hosting agent. You can manage WordPress sites on
          it once multi-server site management lands (Phase D).
        </Banner>
      )}
    </div>
  );
}

function DeployStaticForm({ server, onDone }) {
  const [domain, setDomain] = useState('');
  const [vhost, setVhost] = useState(true);
  const [ssl, setSsl] = useState(true);
  const [email, setEmail] = useState('');
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | uploading | deploying | done | failed
  const [progress, setProgress] = useState(0);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  function startPolling(jobId) {
    setPhase('deploying');
    pollRef.current = setInterval(async () => {
      try {
        const j = await api(`/api/servers/jobs/${jobId}`);
        setJob(j);
        if (j.status === 'done' || j.status === 'failed') {
          clearInterval(pollRef.current);
          setPhase(j.status === 'done' ? 'done' : 'failed');
          onDone();
        }
      } catch {
        /* transient poll error — keep trying until TTL */
      }
    }, 2000);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!file || !domain) return;
    setError(null);
    setPhase('uploading');
    setProgress(0);

    const fd = new FormData();
    fd.append('domain', domain.trim().toLowerCase());
    fd.append('vhost', String(vhost));
    fd.append('ssl', String(ssl));
    if (email) fd.append('certbot_email', email.trim());
    fd.append('archive', file);

    // XHR (not fetch) so we get real upload progress events.
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/servers/${server.id}/deploy-static`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status === 202) {
        const body = JSON.parse(xhr.responseText);
        startPolling(body.job_id);
      } else {
        let msg = `HTTP ${xhr.status}`;
        try { msg = JSON.parse(xhr.responseText).error ?? msg; } catch { /* keep */ }
        setError(msg);
        setPhase('failed');
      }
    };
    xhr.onerror = () => { setError('network_error'); setPhase('failed'); };
    xhr.send(fd);
  }

  const steps = (job?.result ?? job?.error)?.steps ?? [];
  const running = phase === 'uploading' || phase === 'deploying';

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-[12.5px] text-t3">
        Deploy a static site to <span className="font-medium text-t2">{server.name}</span> —
        upload a <span className="font-mono text-t2">.tar.gz</span> of the site files
        (extracted into <span className="font-mono text-t2">/var/www/&lt;domain&gt;</span>).
      </p>

      <div className="flex flex-wrap items-start gap-3">
        <Input
          label="Domain"
          mono
          className="w-56"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="example.com"
          required
          disabled={running}
        />
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium text-t2">Archive (.tar.gz)</span>
          {/* Left as a bare file input: the browser's own control is the only one
              that can open the picker, and restyling it means a fake button plus a
              hidden input for no gain here. */}
          <input
            type="file"
            accept=".tar.gz,.tgz"
            className="block w-56 text-[13px] text-t2 file:mr-2 file:rounded-ctl file:border file:border-bd-strong file:bg-white file:px-2.5 file:py-1.5 file:text-[12px] file:font-medium file:text-t1 hover:file:bg-s-3"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={running}
          />
        </label>
        <Checkbox
          className="mt-[30px]"
          label="Apache vhost"
          checked={vhost}
          onChange={(e) => setVhost(e.target.checked)}
          disabled={running}
        />
        <Checkbox
          className="mt-[30px]"
          label="HTTPS (certbot)"
          checked={ssl}
          onChange={(e) => setSsl(e.target.checked)}
          disabled={running || !vhost}
        />
        {ssl && (
          <Input
            label="Certbot email (optional)"
            mono
            className="w-56"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={running}
          />
        )}
        <Button
          type="submit"
          variant="primary"
          icon={Rocket}
          className="mt-[26px]"
          disabled={running || !file || !domain}
        >
          {phase === 'uploading' ? `Uploading… ${progress}%`
            : phase === 'deploying' ? 'Deploying…'
            : 'Deploy'}
        </Button>
      </div>

      {phase === 'uploading' && (
        <Gauge className="max-w-md" pct={progress} value={`${progress}%`} />
      )}

      <FormError>{error}</FormError>

      {steps.length > 0 && <JobSteps steps={steps} />}

      {phase === 'done' && (
        <Banner compact kind="success" title="Deployed">
          {domain} is live on <span className="font-mono">{server.host}</span>
          {ssl ? ' with HTTPS' : ''}.
        </Banner>
      )}
    </form>
  );
}

/**
 * Per-step result list for a long-running job (bootstrap / static deploy). Local
 * to this page rather than a shared component — the shape (`{ step, ok, detail }`)
 * is the servers job contract, and two call sites is not yet a pattern.
 */
function JobSteps({ steps }) {
  return (
    <ul className="space-y-1 text-[12.5px]">
      {steps.map((s) => (
        <li key={s.step} className="flex items-start gap-2">
          <span className={s.ok ? 'text-ok' : 'text-err'}>{s.ok ? '✓' : '✗'}</span>
          <span className="font-mono text-t1">{s.step}</span>
          {s.detail && (
            <span className="min-w-0 break-all text-t3">
              — {String(s.detail).slice(0, 200)}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Kind is a *category*, not a status — so it gets the neutral Chip treatment,
 * not a state colour. Two state colours next to each other (kind + status) in
 * adjacent cells made it impossible to tell which one meant "something is
 * wrong".
 */
// ─── Bulk delete (2026-10-05) ───────────────────────────────────────────────

const PROXY_BUSY = new Set(['installing', 'removing']);

/** Flagged = something keeps running on the machine, or the panel can't tell (unknown is not zero). */
function isFlagged(s) {
  return !!(s.mail?.on_machine || s.proxy?.on_machine || (s.sites && s.sites.count !== 0));
}

/**
 * Lists every ticked server and what it carries, then asks for the COUNT typed
 * — the same pattern as the cloud bulk delete. The server applies the exact
 * rule of the single delete to each id (backend `removeServer`), so this dialog
 * only has to tell the truth about it:
 *   - the machines are not touched (they keep running, and billing);
 *   - a proxy / mail stack on a machine keeps running, and the panel's controls
 *     for it leave with the row — flagged per server;
 *   - agent WP sites keep running; the panel's route to them leaves.
 * The typed count goes to the server as `confirm_count` — the server is the one
 * that refuses a wrong count, not this form.
 */
function BulkDeleteDialog({ servers, onClose }) {
  const ids = servers.map((s) => s.id);
  const n = ids.length;
  const [preview, setPreview] = useState(null);      // { servers, missing }
  const [previewError, setPreviewError] = useState(null);
  const [typed, setTyped] = useState('');
  const [step, setStep] = useState('confirm');       // confirm | running | done
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const idsKey = ids.join(',');
  const loadPreview = useCallback(async () => {
    setPreview(null);
    setPreviewError(null);
    try {
      setPreview(await api(`/api/servers/delete-preview?ids=${idsKey}`));
    } catch (err) {
      setPreviewError(err instanceof ApiError ? (err.body?.detail ?? err.message) : err.message);
    }
  }, [idsKey]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const rows = preview?.servers ?? [];
  const flagged = rows.filter(isFlagged);

  async function run() {
    setStep('running');
    setError(null);
    try {
      const r = await api('/api/servers', {
        method: 'DELETE',
        body: { ids, confirm_count: Number(typed) },
      });
      setResult(r);
      setStep('done');
    } catch (err) {
      setError(
        err instanceof ApiError && err.body?.error === 'confirm_count_mismatch'
          ? `The count didn't match (${n} selected) — nothing was deleted.`
          : `${err instanceof ApiError ? (err.body?.detail ?? err.message) : err.message} — nothing was deleted.`,
      );
      setStep('confirm');
    }
  }

  const byId = Object.fromEntries(servers.map((s) => [s.id, s]));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-t1/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-delete-title"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl border border-bd bg-white shadow-xl">
        <div className="flex items-center gap-2 border-b border-bd px-5 py-4">
          <Trash2 className="h-4 w-4 text-err" />
          <h2 id="bulk-delete-title" className="flex-1 text-[15px] font-semibold text-t1">
            {step === 'done'
              ? 'Bulk delete — result'
              : `Delete ${n} server${n === 1 ? '' : 's'} from wppanel`}
          </h2>
          {step !== 'running' && (
            <button
              type="button"
              onClick={() => onClose(step === 'done')}
              className="rounded p-0.5 text-t3 hover:bg-s-3"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-[13px] text-t2">
          {step === 'done' && result ? (
            <BulkDeleteResult result={result} byId={byId} />
          ) : (
            <>
              <p className="text-t1">
                <strong>The machines themselves are not touched</strong> — they keep running,
                and billing, at their provider. This only removes them from wppanel.
              </p>

              {previewError && (
                <Banner
                  compact
                  kind="error"
                  title="Couldn't read what these servers carry"
                  className="mt-3"
                  action={<Button size="sm" onClick={loadPreview}>Retry</Button>}
                >
                  {previewError} — not deleting blind. Nothing has changed.
                </Banner>
              )}

              {!preview && !previewError && (
                <div className="mt-4 flex items-center gap-2 text-t3">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Reading what each server carries…
                </div>
              )}

              {preview && (
                <>
                  {flagged.length > 0 && (
                    <p className="mt-3 font-medium text-warn">
                      {flagged.length} of {rows.length} flagged below — something keeps running
                      there after this, or the panel can't tell.
                    </p>
                  )}
                  <ul className="mt-3 divide-y divide-bd rounded-md border border-bd">
                    {rows.map((s) => <BulkDeleteItem key={s.id} s={s} />)}
                  </ul>
                  {preview.missing?.length > 0 && (
                    <p className="mt-2 text-[12.5px] text-t3">
                      Already gone from the panel: {preview.missing.map((id) => byId[id]?.name ?? `#${id}`).join(', ')}
                      {' '}— they will be reported as refused.
                    </p>
                  )}
                  {rows.some((s) => s.mail || s.proxy) && (
                    <p className="mt-2 text-[12.5px] text-t3">
                      Their mail / proxy records stay in panel.db under the old id, out of reach.
                      Registering the same machine again starts blank — it will show no mail or
                      proxy even while they run.
                    </p>
                  )}
                </>
              )}

              {error && <Banner compact kind="error" title="Not deleted" className="mt-3">{error}</Banner>}
            </>
          )}
        </div>

        <div className="flex flex-wrap items-end justify-end gap-2 border-t border-bd px-5 py-3">
          {step === 'done' ? (
            <Button variant="primary" onClick={() => onClose(true)}>Close</Button>
          ) : (
            <>
              <Input
                label={`Type ${n} to confirm`}
                inputMode="numeric"
                value={typed}
                onChange={(e) => setTyped(e.target.value.trim())}
                disabled={step === 'running' || !preview}
                className="mr-auto w-40"
                aria-label={`Type ${n} to confirm`}
              />
              <Button disabled={step === 'running'} onClick={() => onClose(false)}>Cancel</Button>
              <Button
                variant="danger"
                icon={step === 'running' ? Loader2 : Trash2}
                className={step === 'running' ? '[&>svg]:animate-spin' : ''}
                disabled={step === 'running' || !preview || typed === ''}
                onClick={run}
              >
                {step === 'running' ? 'Deleting…' : `Delete ${n} server${n === 1 ? '' : 's'}`}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** One server in the confirm list: identity, then each thing it carries. */
function BulkDeleteItem({ s }) {
  const flagged = isFlagged(s);
  const nothing = !s.mail && !s.proxy && (s.sites === null || s.sites?.count === 0);
  return (
    <li className={['px-3.5 py-3', flagged ? 'bg-warn-bg' : ''].join(' ')}>
      <div className="flex flex-wrap items-center gap-2">
        {flagged && <TriangleAlert className="h-4 w-4 shrink-0 text-warn" aria-label="Flagged" />}
        <span className="font-medium text-t1">{s.name}</span>
        <span className="font-mono text-[12.5px] text-t2">{s.host}</span>
        <KindBadge kind={s.kind} />
      </div>
      <div className="mt-1.5 space-y-1 text-[12.5px] leading-relaxed">
        {nothing && <p className="text-t3">Nothing the panel manages runs on it.</p>}

        {s.proxy && (s.proxy.on_machine ? (
          <p className="text-t1">
            <strong>Proxy {s.proxy.status}</strong>
            {s.proxy.socks_port ? ` (SOCKS5 :${s.proxy.socks_port} · HTTP :${s.proxy.http_port})` : ''}
            {' '}— keeps running on the machine, and the panel's <em>Remove</em> button leaves with
            this row. <strong>Remove the proxy first if you want it gone from the machine.</strong>
            {PROXY_BUSY.has(s.proxy.status) && ' An operation is running on it right now.'}
          </p>
        ) : (
          <p className="text-t3">Proxy: {s.proxy.status} — nothing of it left on the machine.</p>
        ))}

        {s.mail && (s.mail.on_machine ? (
          <p className="text-t1">
            <strong>Mail server {s.mail.status}</strong> ({s.mail.mail_hostname} ·{' '}
            {s.mail.domains} domain{s.mail.domains === 1 ? '' : 's'} · {s.mail.mailboxes} mailbox
            {s.mail.mailboxes === 1 ? '' : 'es'}) — keeps running and receiving mail on the machine;
            the panel's mail controls leave with this row. <strong>Remove the mail first if you
            want it gone:</strong> delete its mailboxes and domains here, then the mail stack itself
            over SSH — the panel has no mail uninstall.
          </p>
        ) : (
          <p className="text-t3">Mail: {s.mail.status} — not installed on the machine.</p>
        ))}

        {s.kind === 'agent' && s.sites && (s.sites.count === null ? (
          <p className="text-t1">
            <strong>WordPress sites: unknown</strong> — the agent didn't answer ({s.sites.error}).
            Whatever it hosts keeps running.
          </p>
        ) : s.sites.count > 0 ? (
          <p className="text-t1">
            <strong>{s.sites.count} WordPress site{s.sites.count === 1 ? '' : 's'}</strong>
            {' '}({s.sites.domains.slice(0, 5).join(', ')}{s.sites.count > 5 ? ', …' : ''}) — keep
            running on the agent; the panel's way to manage them leaves with this row.
          </p>
        ) : (
          <p className="text-t3">No WordPress sites on the agent.</p>
        ))}
      </div>
    </li>
  );
}

/** Per server: deleted · refused (why). A partial is said as a partial. */
function BulkDeleteResult({ result, byId }) {
  const REASONS = {
    server_not_found: 'not found — already deleted?',
    delete_failed: 'the database refused the delete',
  };
  return (
    <>
      <p className={['font-medium', result.refused ? 'text-warn' : 'text-ok'].join(' ')}>
        Deleted {result.deleted} of {result.requested}
        {result.refused ? ` · ${result.refused} refused` : ''}.
      </p>
      <ul className="mt-3 divide-y divide-bd rounded-md border border-bd">
        {result.results.map((r) => (
          <li key={r.id} className="flex items-start gap-2 px-3.5 py-2.5">
            {r.ok
              ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok" />
              : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-err" />}
            <div className="min-w-0">
              <span className="font-medium text-t1">{r.name ?? byId[r.id]?.name ?? `#${r.id}`}</span>
              {' — '}
              {r.ok ? (
                <span className="text-t2">deleted from wppanel</span>
              ) : (
                <span className="text-err">
                  refused: {REASONS[r.error] ?? r.error}
                  {r.detail ? ` (${r.detail})` : ''}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function KindBadge({ kind }) {
  return (
    <span className="inline-flex items-center rounded-ctl border border-bd-strong bg-s-3 px-2 py-[3px] font-mono text-[11.5px] text-t2">
      {kind === 'agent' ? 'agent (WP)' : 'ssh (static)'}
    </span>
  );
}

/**
 * Server reachability, on the panel's own vocabulary. Not the shared Pill:
 * `servers.status` is `ok` / `unreachable` / `new`, which is a third vocabulary
 * on top of Pill's liveness and billing sets. Adding three more states there for
 * one call site would make Pill a dumping ground; if a second page ever needs
 * these, move them in then.
 */
function StatusBadge({ status }) {
  const styles = {
    ok: { label: 'Reachable', className: 'bg-s-green text-[#046B57]' },
    unreachable: { label: 'Unreachable', className: 'bg-err-bg text-err' },
    new: { label: 'Not tested', className: 'bg-s-3 text-t2' },
  };
  const spec = styles[status] ?? { label: status, className: 'bg-s-3 text-t2' };
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-xl px-[9px] py-1 text-[11.5px] font-medium',
        spec.className,
      ].join(' ')}
    >
      <i className="block w-1.5 h-1.5 rounded-full bg-current shrink-0" aria-hidden="true" />
      {spec.label}
    </span>
  );
}

function AddServerForm({ onCreated }) {
  const [form, setForm] = useState({
    name: '', kind: 'ssh', host: '', ssh_port: 22, ssh_user: 'root',
    ssh_key: '', ssh_password: '', agent_url: '', agent_secret: '', notes: '',
  });
  const [authMethod, setAuthMethod] = useState('key'); // 'key' | 'password'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        name: form.name,
        kind: form.kind,
        host: form.host,
        ssh_port: Number(form.ssh_port) || 22,
        ssh_user: form.ssh_user || 'root',
        notes: form.notes || null,
      };
      if (authMethod === 'key' && form.ssh_key) body.ssh_key = form.ssh_key;
      if (authMethod === 'password' && form.ssh_password) body.ssh_password = form.ssh_password;
      if (form.kind === 'agent') {
        body.agent_url = form.agent_url;
        if (form.agent_secret) body.agent_secret = form.agent_secret;
      }
      await api('/api/servers', { method: 'POST', body });
      onCreated();
    } catch (err) {
      const detail = err.body?.detail;
      setError(Array.isArray(detail) ? detail.join(' · ') : err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Add a server"
        description="Registered here, then tested before anything is deployed to it."
      />
      <form onSubmit={handleSubmit} className="space-y-4 p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input label="Name" value={form.name} onChange={set('name')} placeholder="hetzner-wp-2" required />
          <Select label="Kind" value={form.kind} onChange={set('kind')}>
            <option value="ssh">ssh — static deploys</option>
            <option value="agent">agent — full WP management</option>
          </Select>
          <Input label="Host (IP)" mono value={form.host} onChange={set('host')} placeholder="1.2.3.4" required />
          <Input label="SSH port" mono type="number" value={form.ssh_port} onChange={set('ssh_port')} />
          <Input label="SSH user" mono value={form.ssh_user} onChange={set('ssh_user')} />
          {form.kind === 'agent' && (
            <Input label="Agent URL" mono value={form.agent_url} onChange={set('agent_url')} placeholder="http://1.2.3.4:3002" />
          )}
        </div>

        <div className="space-y-2">
          <RadioGroup label="SSH auth">
            <Radio
              name="auth_method"
              label="private key"
              checked={authMethod === 'key'}
              onChange={() => setAuthMethod('key')}
            />
            <Radio
              name="auth_method"
              label="root password"
              checked={authMethod === 'password'}
              onChange={() => setAuthMethod('password')}
            />
          </RadioGroup>
          {authMethod === 'key' ? (
            <Textarea
              label={`SSH private key${form.kind === 'agent' ? ' (for bootstrap — optional for now)' : ''}`}
              mono
              rows={5}
              value={form.ssh_key}
              onChange={set('ssh_key')}
              placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
            />
          ) : (
            <Input
              label="SSH password"
              mono
              type="password"
              autoComplete="off"
              className="sm:max-w-sm"
              value={form.ssh_password}
              onChange={set('ssh_password')}
              placeholder="stored encrypted — works everywhere a key does"
            />
          )}
        </div>

        {form.kind === 'agent' && (
          <Input
            label="Agent JWT secret"
            mono
            value={form.agent_secret}
            onChange={set('agent_secret')}
            placeholder="shared secret (from agent .env)"
          />
        )}

        <Input label="Notes" value={form.notes} onChange={set('notes')} placeholder="optional" />

        <FormError>{error}</FormError>

        <Button type="submit" variant="primary" disabled={submitting}>
          {submitting ? 'Adding…' : 'Add server'}
        </Button>
      </form>
    </Card>
  );
}
